import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Select from "react-select";
import AdminServices from "services/admin-services";
import { setToaster } from "common/toastStore";
import { useState } from "react";
import Loader from "common/Loader";

interface PackageAddDocTypesProps {
  onHide: () => void;
  selectedRow: any;
  onSuccess: () => Promise<void> | void;
  packageData: any;
}

interface FormValues {
  documentType: string;
  processOrder: string;
  required: "YES" | "NO";
}

const requiredOptions = [
  { label: "Yes", value: "YES" },
  { label: "No", value: "NO" },
];

const normalizeName = (value: string) => value.trim().toLowerCase();

const validationSchema = Yup.object({
  documentType: Yup.string().trim().required("Document Type is required"),
  processOrder: Yup.string()
    .trim()
    .required("Process Order is required")
    .matches(/^\d+$/, "Must be a number"),
  required: Yup.string().required("Required field is mandatory"),
});

const AddDocumentTypes = ({
  onHide,
  selectedRow,
  onSuccess,
  packageData,
}: PackageAddDocTypesProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    mode: "all",
    defaultValues: {
      documentType: "",
      processOrder: "",
      required: "" as any,
    },
  });

  const documentType = watch("documentType");
  const processOrder = watch("processOrder");
  const requiredValue = watch("required");

  const existingDocumentTypes = Array.isArray(selectedRow) ? selectedRow : [];

  const onSubmit = (data: FormValues) => {
    const duplicateExists = existingDocumentTypes.some(
      (item: any) =>
        normalizeName(item?.name || "") === normalizeName(data.documentType)
    );

    if (duplicateExists) {
      setToaster({
        severity: "warn",
        summary: "Duplicate Document Type",
        detail: "Document type already exists.",
      });
      return;
    }

    setLoading(true);
    const payload = {
      packageId: selectedRow?.[0]?.packageId ?? packageData?.id,
      name: data?.documentType,
      description: data?.documentType,
      processOrder: data?.processOrder,
      required: data?.required === "YES" ? true : false,
      isActive: true,
    };
    AdminServices.addPackageDocTypes(payload)
      .then(async () => {
        await onSuccess();
        setLoading(false);
        setToaster({
          severity: "success",
          summary: "Success",
          detail: `Package added successfully!`,
        });
        onHide();
        reset();
      })
      .catch((e: Error) => {
        console.warn(e);
        setLoading(false);
        setToaster({
          severity: "error",
          summary: "Oops",
          detail: `Something went wrong, try again!`,
        });
      });
  };

  const handleRequiredChange = (selected: any) => {
    setValue("required", selected?.value, { shouldValidate: true });
  };

  return (
    <div className="py-5">
      <form onSubmit={handleSubmit(onSubmit)} className="py-5">
        <div className="grid grid-cols-1 gap-8">
          {/* Document Type */}
          <div>
            <div className="p-float-label">
              <InputText
                id="documentType"
                value={documentType || ""}
                {...register("documentType")}
                className="w-full text-sm"
              />
              <label htmlFor="documentType">Document Type</label>
            </div>
            {errors.documentType && (
              <div className="text-xs text-red-900">
                {errors.documentType.message}
              </div>
            )}
          </div>

          {/* Process Order */}
          <div>
            <div className="p-float-label">
              <InputText
                id="processOrder"
                value={processOrder || ""}
                {...register("processOrder")}
                className="w-full text-sm"
              />
              <label htmlFor="processOrder">Process Order</label>
            </div>
            {errors.processOrder && (
              <div className="text-xs text-red-900">
                {errors.processOrder.message}
              </div>
            )}
          </div>

          {/* Required */}
          <div>
            <label htmlFor="required" className="pb-2 block text-sm">
              Required
            </label>
            <Select
              options={requiredOptions}
              placeholder="Required Type"
              value={requiredOptions.find((opt) => opt.value === requiredValue)}
              onChange={handleRequiredChange}
            />
            {errors.required && (
              <div className="text-xs text-red-900">
                {errors.required.message}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-row-reverse gap-4">
            <Button
              type="submit"
              label="Save"
              className="p-button-sm p-button-outlined p-button-success"
            />
            <Button
              type="button"
              onClick={() => {
                reset();
                onHide();
              }}
              label="Cancel"
              className="p-button-sm p-button-outlined p-button-warning"
            />
          </div>
        </div>
      </form>
      {loading && <Loader />}
    </div>
  );
};

export default AddDocumentTypes;
